import { Injectable, isDevMode } from '@angular/core';
import { LogLevel } from '../shared/enums/log-level.enum';
import * as Sentry from "@sentry/angular";
import { environment } from 'src/environments/environment';

/** Centralized logger service to handle all logging to console and Sentry. */
@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private readonly levelOrder: LogLevel[] = [
    LogLevel.DEBUG,
    LogLevel.INFO,
    LogLevel.WARNING,
    LogLevel.ERROR,
    LogLevel.FATAL
  ];

  private readonly minLevel: LogLevel = environment.logLevel;

  /**
   * Main logging handler. Outputs to console and optionally forwards to Sentry.
   * @param level Log severity level
   * @param message Main message string
   * @param meta Optional metadata (shown in console and attached to Sentry)
   * @param forceToSentry Forces sending this log to Sentry, even if level is not ERROR/FATAL
   */
  log(level: LogLevel, message: string, meta: any = {}, forceToSentry: boolean = false): void {
    const shouldLogToConsole = this.shouldLog(level);

    if (shouldLogToConsole) {
      const formattedMessage = `[${level}]: ${message}`;
      const shouldIncludeMeta = meta && Object.keys(meta).length > 0;

      switch (level) {
        case LogLevel.DEBUG:
          shouldIncludeMeta ? console.debug(formattedMessage, meta) : console.debug(formattedMessage);
          break;
          
        case LogLevel.INFO:
          shouldIncludeMeta ? console.info(formattedMessage, meta) : console.info(formattedMessage);
          break;

        case LogLevel.WARNING:
          shouldIncludeMeta ? console.warn(formattedMessage, meta) : console.warn(formattedMessage);
          break;

        case LogLevel.ERROR:
        case LogLevel.FATAL:
          shouldIncludeMeta ? console.error(formattedMessage, meta) : console.error(formattedMessage);
          break;
      }
    }

    // Always send to Sentry if level is ERROR or FATAL or if forceToSentry is explicitly true
    if (forceToSentry || level === LogLevel.ERROR || level === LogLevel.FATAL) {
      const sentryMethod = level === LogLevel.ERROR || level === LogLevel.FATAL
        ? Sentry.captureException
        : Sentry.captureMessage;

      sentryMethod(message, { level, extra: meta });

    }
  }

  /**
   * Logs a debug-level message.
   * Used for detailed diagnostic information useful during development.
   *
   * @param msg - The message to log.
   * @param meta - Optional metadata to attach (e.g., context, variables).
   * @param forceToSentry - Whether to force-send the message to Sentry even if it's not an error or fatal.
   */
  debug(msg: string, meta: any = {}, forceToSentry = false) {
    this.log(LogLevel.DEBUG, msg, meta, forceToSentry);
  }

  /**
   * Logs an informational message.
   * Used to report normal application behavior (e.g., user actions, process milestones).
   *
   * @param msg - The message to log.
   * @param meta - Optional metadata to attach for context.
   * @param forceToSentry - Whether to send this info log to Sentry manually.
   */
  info(msg: string, meta: any = {}, forceToSentry = false) {
    this.log(LogLevel.INFO, msg, meta, forceToSentry);
  }

  /**
   * Logs a warning message.
   * Used to indicate potential problems or non-critical issues.
   *
   * @param msg - The warning message to log.
   * @param meta - Optional metadata providing additional context.
   * @param forceToSentry - Whether to send this warning to Sentry manually.
   */
  warn(msg: string, meta: any = {}, forceToSentry = false) {
    this.log(LogLevel.WARNING, msg, meta, forceToSentry);
  }

  /**
   * Logs an error message.
   * Used for actual runtime errors or failures that should be captured and monitored.
   *
   * @param msg - The error message.
   * @param meta - Optional metadata providing error context (e.g., user ID, request ID).
   * @param forceToSentry - Whether to ensure this error is sent to Sentry (default is already true).
   */
  error(msg: string, meta: any = {}, forceToSentry = false) {
    this.log(LogLevel.ERROR, msg, meta, forceToSentry);
  }

  /**
   * Logs a fatal message.
   * Used for critical issues that may require immediate attention or cause application crashes.
   *
   * @param msg - The fatal message.
   * @param meta - Optional metadata for deeper diagnostic insight.
   * @param forceToSentry - Whether to send this fatal log to Sentry (default is already true).
   */
  fatal(msg: string, meta: any = {}, forceToSentry = false) {
    this.log(LogLevel.FATAL, msg, meta, forceToSentry);
  }

  /**
   * Checks if the given log level is allowed based on current environment settings.
   */
  private shouldLog(level: LogLevel): boolean {
    return this.levelOrder.indexOf(level) >= this.levelOrder.indexOf(this.minLevel);
  }

}
